import { useState, useCallback } from "react";
import { Scissors, Upload, Download, Trash2, FileText, Loader2, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import axios from "axios";
import SEOHead from "@/components/SEOHead";
import FAQSection from "@/components/FAQSection";
import RelatedTools from "@/components/RelatedTools";
import ToolContentSection from "@/components/ToolContentSection";
import { toolContent } from "@/data/toolContent";
import { downloadFile, downloadZip } from "@/utils/downloadHelper";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const faqs = [
  {
    question: "Is this PDF splitter free to use?",
    answer: "Yes, completely free with no signup required. Split unlimited PDFs into individual pages or custom ranges."
  },
  {
    question: "Can I extract specific pages from a PDF?",
    answer: "Yes, you can split all pages into separate files or select specific pages/ranges to extract (e.g., 1, 3, 5-10)."
  },
  {
    question: "Will splitting affect the quality of my PDF?",
    answer: "No, the original quality is preserved. We extract pages without re-encoding or compressing the content."
  },
  {
    question: "What's the maximum file size I can split?",
    answer: "You can upload PDFs up to 50MB. For larger files, consider compressing first."
  },
  {
    question: "Are my files secure?",
    answer: "Yes, all files are automatically deleted after 30 minutes. We never store or access your documents."
  }
];

const PdfSplitPage = () => {
  const [file, setFile] = useState(null);
  const [splitMode, setSplitMode] = useState("all");
  const [pageRange, setPageRange] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [results, setResults] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectedPages, setSelectedPages] = useState(new Set());

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const loadPreview = async (pdfFile) => {
    setIsLoadingPreview(true);
    const formData = new FormData();
    formData.append("file", pdfFile);

    try {
      const response = await axios.post(`${API}/pdf-preview`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setPreview(response.data);
      }
    } catch (error) {
      console.error("Preview error:", error);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === "application/pdf") {
      setFile(droppedFile);
      setResults(null);
      setPreview(null);
      setSelectedPages(new Set());
      await loadPreview(droppedFile);
    } else {
      toast.error("Please upload a PDF file");
    }
  }, []);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
      setResults(null);
      setPreview(null);
      setSelectedPages(new Set());
      await loadPreview(selectedFile);
    } else {
      toast.error("Please upload a PDF file");
    }
  };

  const togglePageSelection = (pageNum) => {
    const newSelected = new Set(selectedPages);
    if (newSelected.has(pageNum)) {
      newSelected.delete(pageNum);
    } else {
      newSelected.add(pageNum);
    }
    setSelectedPages(newSelected);
    // Update page range input
    const sorted = Array.from(newSelected).sort((a, b) => a - b);
    setPageRange(sorted.join(", "));
  };

  const handleSplit = async () => {
    if (!file) {
      toast.error("Please select a PDF file first");
      return;
    }

    if (splitMode === "custom" && !pageRange.trim()) {
      toast.error("Please enter page numbers or ranges");
      return;
    }

    setIsProcessing(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("pages", splitMode === "all" ? "all" : pageRange);

    try {
      const response = await axios.post(`${API}/pdf-split`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setResults(response.data);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Split failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async (fileId, filename) => {
    try {
      await downloadFile(`${API}/download/${fileId}`, filename);
    } catch (error) {
      toast.error("Download failed");
    }
  };

  const handleDownloadAll = async () => {
    if (results?.files?.length > 0) {
      try {
        const folderId = results.files[0].id.split("/")[0];
        await downloadZip(`${API}/download-all/${folderId}`, "split-pages.zip");
      } catch (error) {
        toast.error("Download failed");
      }
    }
  };

  const handleReset = () => {
    setFile(null);
    setResults(null);
    setPageRange("");
    setSplitMode("all");
    setPreview(null);
    setSelectedPages(new Set());
  };

  return (
    <>
      <SEOHead
        title="Split PDF Online Free | Extract Pages"
        description="Split PDF into individual pages or extract specific pages. Free online PDF splitter. No signup required. Select pages visually."
        canonicalPath="/pdf-split"
        keywords="split PDF, extract PDF pages, PDF splitter free, separate PDF pages, online PDF split"
      />
      
      <div className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 animate-fade-in-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-black flex items-center justify-center">
                <Scissors className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-3xl md:text-5xl font-semibold tracking-tight">
                Split PDF Online Free
              </h1>
            </div>
            <p className="text-muted-foreground text-lg max-w-2xl">
              Extract specific pages or split your PDF into individual files. Click thumbnails to select pages visually. No signup required.
            </p>
          </div>

        {/* Upload Area */}
        {!results && (
          <div className="space-y-6 animate-fade-in-up animate-delay-100">
            <div
              className={`upload-zone ${isDragging ? "dragging" : ""}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById("file-input").click()}
              data-testid="upload-zone"
            >
              <input
                id="file-input"
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              {file ? (
                <div className="text-center">
                  <div className="w-16 h-16 bg-secondary flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-[#FF3B30]" />
                  </div>
                  <p className="font-medium text-lg">{file.name}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                    {preview && ` • ${preview.total_pages} pages`}
                  </p>
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 bg-secondary flex items-center justify-center">
                    <Upload className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-lg">Drop your PDF here</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      or click to browse (max 50MB)
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Preview Section */}
            {isLoadingPreview && (
              <div className="bg-card border border-border p-6 text-center">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-[#FF3B30]" />
                <p className="text-sm text-muted-foreground">Loading preview...</p>
              </div>
            )}

            {preview && !isLoadingPreview && (
              <div className="bg-card border border-border p-4" data-testid="preview-section">
                <div className="flex items-center gap-2 mb-3">
                  <Eye className="w-4 h-4 text-[#FF3B30]" />
                  <span className="text-sm font-medium">
                    Preview ({preview.total_pages} pages)
                    {splitMode === "custom" && selectedPages.size > 0 && (
                      <span className="text-[#FF3B30] ml-2">• {selectedPages.size} selected</span>
                    )}
                  </span>
                </div>
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                  {preview.pages.map((page, index) => (
                    <div
                      key={index}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (splitMode === "custom") {
                          togglePageSelection(page.page);
                        }
                      }}
                      className={`relative aspect-[3/4] bg-secondary overflow-hidden border-2 transition-all cursor-pointer ${
                        splitMode === "custom" && selectedPages.has(page.page)
                          ? "border-[#FF3B30] ring-2 ring-[#FF3B30]/30"
                          : "border-border hover:border-black/50"
                      }`}
                    >
                      <img
                        src={`${API}/download/${preview.preview_id}/${page.thumbnail.split('/').pop()}`}
                        alt={`PDF page ${page.page} thumbnail - click to select for extraction`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                      <span className={`absolute bottom-0 left-0 right-0 text-white text-xs text-center py-0.5 ${
                        splitMode === "custom" && selectedPages.has(page.page)
                          ? "bg-[#FF3B30]"
                          : "bg-black/60"
                      }`}>
                        {page.page}
                      </span>
                    </div>
                  ))}
                </div>
                {splitMode === "custom" && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Click pages to select/deselect for extraction
                  </p>
                )}
              </div>
            )}

            {/* Split Options */}
            <div className="border border-border p-6 space-y-4">
              <h3 className="font-medium text-lg">Split Options</h3>
              <RadioGroup
                value={splitMode}
                onValueChange={(val) => {
                  setSplitMode(val);
                  if (val === "all") {
                    setSelectedPages(new Set());
                    setPageRange("");
                  }
                }}
                className="space-y-3"
                data-testid="split-mode-radio"
              >
                <div className="flex items-center space-x-3">
                  <RadioGroupItem value="all" id="all" data-testid="split-all" />
                  <Label htmlFor="all" className="cursor-pointer">
                    Split all pages (each page becomes a separate PDF)
                  </Label>
                </div>
                <div className="flex items-center space-x-3">
                  <RadioGroupItem value="custom" id="custom" data-testid="split-custom" />
                  <Label htmlFor="custom" className="cursor-pointer">
                    Extract specific pages (click thumbnails above or type below)
                  </Label>
                </div>
              </RadioGroup>

              {splitMode === "custom" && (
                <div className="mt-4 space-y-2">
                  <Label htmlFor="page-range" className="text-sm font-mono uppercase tracking-wide text-muted-foreground">
                    Page Numbers
                  </Label>
                  <Input
                    id="page-range"
                    value={pageRange}
                    onChange={(e) => setPageRange(e.target.value)}
                    placeholder="e.g., 1, 3, 5-10, 15"
                    className="rounded-sm"
                    data-testid="page-range-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter page numbers separated by commas, or ranges with hyphens
                  </p>
                </div>
              )}
            </div>

            {/* Split Button */}
            <Button
              onClick={handleSplit}
              disabled={!file || isProcessing}
              className="w-full h-14 bg-black hover:bg-black/90 text-white rounded-sm btn-hover-lift text-lg font-medium"
              data-testid="split-button"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Splitting...
                </>
              ) : (
                <>
                  <Scissors className="w-5 h-5 mr-2" />
                  Split PDF
                </>
              )}
            </Button>
          </div>
        )}

        {/* Results */}
        {results && (
          <div className="space-y-6 animate-fade-in-up" data-testid="results-section">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold tracking-tight">
                Split Complete
              </h2>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="rounded-sm"
                  data-testid="reset-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Start Over
                </Button>
                <Button
                  onClick={handleDownloadAll}
                  className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 rounded-sm btn-hover-lift"
                  data-testid="download-all-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download All (ZIP)
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              {results.files.map((file, index) => (
                <div
                  key={file.id}
                  className="file-item animate-fade-in-up"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`result-file-${index}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-secondary flex items-center justify-center">
                      <FileText className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium">{file.filename}</p>
                      <p className="text-sm text-muted-foreground">Page {file.page}</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(file.id, file.filename)}
                    className="rounded-sm"
                    data-testid={`download-file-${index}`}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Download
                  </Button>
                </div>
              ))}
            </div>

            <div className="bg-secondary p-4 text-center">
              <p className="text-sm text-muted-foreground">
                ⏱ Files will be automatically deleted in 30 minutes
              </p>
            </div>
          </div>
        )}

        {/* FAQ Section */}
        <ToolContentSection toolKey="pdf-split" content={toolContent["pdf-split"]} />
        <FAQSection faqs={faqs} />

        {/* Related Tools */}
        <RelatedTools currentTool="pdf-split" />
      </div>
    </div>
    </>
  );
};

export default PdfSplitPage;
